/*  eslint-disable react/prop-types */

import React, { useMemo, useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import connect from '@app/utils/connect';
import sendNotification from '@app/actions/notifications/send';
import clearAllNotifs from '@app/actions/notifications/clearAll';
import combineActions from '@app/utils/combineActions';
import usePacingGoals from '@app/components/hooks/usePacingGoals';
import useClientRsps from '@app/components/hooks/useClientRsps';
import usePlacements from '@app/components/hooks/usePlacements';
import { useRightsholderAttribution } from '@app/components/hooks/useRightsholderAttribution';
import { getRHOptions } from '@app/components/filters/getSpecificOptions';
import generateReportTitle from './generateReportTitle';
import { isEmpty } from 'ramda';
import dayjs from 'dayjs';

import {
    PACING_VIEW_GOALS,
    REPORTS_BROADCAST_COUNTRY_FILTER,
} from '@app/constants/config';
import { normalizeBroadcastCountries } from './broadcastCountries';

export const ReportsContext = React.createContext(null);

export const resolveSelectedRightsholders = (
    treeSelection: any[] = [],
    rhTreeData: any[] = [],
) => {
    const rhIds: string[] = [];

    treeSelection.forEach(id => {
        if (String(id).startsWith('league_')) {
            const leagueId = String(id).split('_')[1];
            const group = rhTreeData.find(
                g => String(g.value) === `league_${leagueId}`,
            );

            rhIds.push(
                ...(group?.children?.map(child => String(child.value)) ?? []),
            );
        } else {
            rhIds.push(String(id));
        }
    });

    return rhIds;
};

export type FormValues = {
    reportType: string;
    dateSelectionType: string;
    placementSelectionType: string;
    includesMethodologySlides: boolean;
    startDate: dayjs.Dayjs | null;
    endDate: dayjs.Dayjs | null;
    goal: string;
    selectedTreeRightsholders: any[];
    selectedRightsholders: any;
    rightsholders: string[];
    rightsholdersLeaguePairs: Record<string, boolean> | any[];
    sponsors: string[];
    placement: number;
    filteredPlacements: string[];
    broadcastCountryIds: number[];
    broadcastCountryNames: string[];
    reportTitle: string;
    postImgUrl?: string;
};

export const ReportsProvider = ({
    children,
    client,
    filters,
    sendNotification,
    clearAllNotifs,
    closeDrawer,
    showDrawer,
    isDrawerOpen,
    addScheduledReport,
    setActiveTable,
    user,
    scheduleHookConfig,
    openScheduleModalForCreate,
    closeScheduleModal,
    isCreateMode,
}) => {
    const [isApplyingGoal, setIsApplyingGoal] = useState(false);
    const [availableMedia, setAvailableMedia] = useState({});
    const [isLoadingPosts, setIsLoadingPosts] = useState(false);
    const [searchDone, setSearchDone] = useState(false);

    const canViewPacing = client.permissions.includes(PACING_VIEW_GOALS);
    const hasReportsBroadcastCountryFilter = client.permissions.includes(
        REPORTS_BROADCAST_COUNTRY_FILTER,
    );

    const { rightsholderAttribution } = useRightsholderAttribution({
        client,
    });

    const { values, setFieldValue, setValues } = useFormikContext<FormValues>();

    const { data: goals = [] } = usePacingGoals({
        clientId: client.id,
        enabled: canViewPacing,
    });

    const { data: clientRsps } = useClientRsps(client.id, client);

    // Build a map: RH ID → sponsor[]
    const sponsorMap = useMemo(() => {
        const map: Record<number, Record<number, string>> = {};

        if (!Array.isArray(clientRsps)) return map;

        for (const rsp of clientRsps) {
            const rhId = rsp.rightsholder.id;
            const sponsor = rsp.sponsor;

            if (!map[rhId]) map[rhId] = {};
            map[rhId][sponsor.id] = sponsor.name;
        }

        return map;
    }, [clientRsps]);

    const rightsholdersById = useMemo(() => {
        return filters?.rightsholders?.list || {};
    }, [filters]);

    const sponsorsById = useMemo(() => {
        const byId = {};
        clientRsps?.forEach(rsp => {
            byId[rsp.sponsor.id] = rsp.sponsor;
        });
        return byId;
    }, [clientRsps]);

    const goalsOptions = goals.map(goal => ({
        label: goal.name,
        value: goal.id,
    }));

    useEffect(() => {
        if (!values.goal || !goals || values.reportType !== 'pacing') return;

        // Auto-filling other fields based on selected pacing goal
        const selectedGoal = goals.find(g => g.id === values.goal);
        if (!selectedGoal) return;
        setFieldValue('goalName', selectedGoal.name, false);
        setIsApplyingGoal(true); // lock interaction during patching

        const {
            filter: { rightsholderIds, sponsorIds, rightsholderPlacementIds },
            startDate,
            endDate,
        } = selectedGoal;

        const sponsors = sponsorIds.map(String);
        const filteredPlacements = rightsholderPlacementIds;
        const selectedRH = String(rightsholderIds[0]);

        setFieldValue('rightsholders', [selectedRH], false);
        setFieldValue('selectedTreeRightsholders', [selectedRH], false);
        setFieldValue('sponsors', sponsors, false);
        setFieldValue('placement', null, false); // always null for pacing
        setFieldValue('placementSelectionType', 'all', false);
        setFieldValue('filteredPlacements', filteredPlacements, false);
        setFieldValue('startDate', startDate ? dayjs(startDate) : null, false);
        setFieldValue('endDate', endDate ? dayjs(endDate) : null, false);
        setFieldValue(
            'dateSelectionType',
            startDate || endDate ? 'specific' : 'all',
            false,
        );

        // Unlock UI interaction after short delay (to let filters settle)
        setTimeout(() => setIsApplyingGoal(false), 300);
    }, [values.goal, goals, values.reportType]);

    const rightsholdersOptions = useMemo(() => {
        if (filters.isLoading || isEmpty(filters.rightsholders.list)) {
            return [];
        }

        return getRHOptions({
            rhList: filters.rightsholders.list,
            shouldShowLeagueHierarchy: filters.shouldShowLeagueHierarchy,
        });
    }, [filters]);

    const rhTreeData = useMemo(() => {
        return rightsholdersOptions.map(option => ({
            value: `league_${option.value}`, // parent node
            title: option.label,
            children:
                option.nested?.map(nested => ({
                    value: nested.value, // this is the RH id
                    title: nested.label,
                })) ?? [],
        }));
    }, [rightsholdersOptions]);

    const onChangeRightsholders = treeSelection => {
        const rightsholders = resolveSelectedRightsholders(
            treeSelection,
            rhTreeData,
        );

        // Keep tree, submission, and dependent filters in one Formik update.
        // The updater form reads the latest form state instead of the values
        // captured when this callback was created, so a concurrent field
        // update is not clobbered.
        setValues(
            prev => ({
                ...prev,
                selectedTreeRightsholders: treeSelection,
                rightsholders,
                sponsors: [],
                placement: null,
            }),
            false,
        );
    };

    const selectedRightsholders = useMemo(() => {
        return resolveSelectedRightsholders(
            values.selectedTreeRightsholders,
            rhTreeData,
        );
    }, [values.selectedTreeRightsholders, rhTreeData]);

    const { data: placementsData = [], isLoading: isLoadingPlacements } =
        usePlacements({
            clientId: client.id,
            rhIds: selectedRightsholders.map(Number),
            client,
        });

    const [placementOptions, filteredPlacements] = useMemo(() => {
        const allOptions: { value: string | number; label: string }[] = [];
        const allFiltered: string[] = [];

        placementsData.forEach(({ rightsholderId, placements = [] }) => {
            const globalFilterList =
                filters?.rightsholders?.list?.[rightsholderId]
                    ?.rightsholderPlacements || [];
            const globalIds = globalFilterList.map(rp =>
                parseInt(rp.split('-')[1]),
            );

            const valid = (Array.isArray(placements) ? placements : []).filter(
                p => globalIds.includes(p.rightsholderPlacement?.id),
            );

            const formatted = valid.map(p => {
                const id = p.rightsholderPlacement?.id;
                const name = p.customName || p.rightsholderPlacement?.name;

                allFiltered.push(`${rightsholderId}-${id}`);
                return { id, name };
            });

            const hashtags = formatted.filter(p =>
                p.name.endsWith('- Hashtag'),
            );
            const mentions = formatted.filter(p =>
                p.name.endsWith('- Mention'),
            );
            const keywords = formatted.filter(p =>
                p.name.endsWith('- Keyword'),
            );
            const rest = formatted.filter(
                p =>
                    !p.name.endsWith('- Hashtag') &&
                    !p.name.endsWith('- Mention') &&
                    !p.name.endsWith('- Keyword'),
            );

            allOptions.push(
                ...rest.map(p => ({ value: p.id, label: p.name })),
                hashtags.length && {
                    value: hashtags.map(p => p.id).join(','),
                    label: 'Hashtag Text Exposure',
                },
                mentions.length && {
                    value: mentions.map(p => p.id).join(','),
                    label: 'Mention Text Exposure',
                },
                keywords.length && {
                    value: keywords.map(p => p.id).join(','),
                    label: 'Keyword Text Exposure',
                },
            );
        });

        return [
            allOptions
                .filter(Boolean)
                .sort((a, b) => a.label.localeCompare(b.label)),
            allFiltered,
        ];
    }, [placementsData, filters]);

    useEffect(() => {
        const treeSelection = values.selectedTreeRightsholders || [];

        const rhLeaguePairs = {};

        treeSelection.forEach(id => {
            if (String(id).startsWith('league_')) {
                const leagueId = String(id).split('_')[1];

                const group = rhTreeData.find(
                    g => String(g.value) === `league_${leagueId}`,
                );

                const children =
                    group?.children?.map(child => String(child.value)) || [];

                children.forEach(rhId => {
                    rhLeaguePairs[`${leagueId}-${rhId}`] = true;
                });
            } else {
                // selected as RH, find parent league
                const leagueGroup = rhTreeData.find(group =>
                    group.children?.some(
                        child => String(child.value) === String(id),
                    ),
                );

                if (leagueGroup) {
                    const leagueId = String(leagueGroup.value).split('_')[1];
                    rhLeaguePairs[`${leagueId}-${id}`] = true;
                }
            }
        });

        if (
            JSON.stringify(values.rightsholdersLeaguePairs) !==
            JSON.stringify(rhLeaguePairs)
        ) {
            setFieldValue('rightsholdersLeaguePairs', rhLeaguePairs, false);
        }
    }, [values.selectedTreeRightsholders, rhTreeData]);

    useEffect(() => {
        if (!values.rightsholders?.length) return;

        const reportTitle = generateReportTitle(values, {
            rightsholdersById,
            sponsorsById,
            clientName: client.name,
        });

        if (values.reportTitle !== reportTitle) {
            setFieldValue('reportTitle', reportTitle, false);
        }
    }, [
        values.rightsholders,
        values.sponsors,
        values.startDate,
        values.endDate,
        rightsholdersById,
        sponsorsById,
        client.name,
    ]);

    useEffect(() => {
        if (
            values.placementSelectionType === 'single' ||
            JSON.stringify(values.filteredPlacements || []) ===
                JSON.stringify(filteredPlacements)
        )
            return;

        setFieldValue('filteredPlacements', filteredPlacements, false);
    }, [
        filteredPlacements,
        values.placementSelectionType,
        values.filteredPlacements,
    ]);

    useEffect(() => {
        if (
            selectedRightsholders.length > 1 &&
            values.placementSelectionType !== 'all'
        ) {
            setFieldValue('placementSelectionType', 'all', false);
            setFieldValue('placement', null, false);
        }
    }, [selectedRightsholders, values.placementSelectionType]);

    const sponsorOptions = useMemo(() => {
        const sponsorEntries = selectedRightsholders.flatMap(rhId =>
            Object.entries(sponsorMap[rhId] || {}),
        );

        const uniqueSponsors = Object.fromEntries(sponsorEntries);

        return Object.entries(uniqueSponsors as Record<string, string>)
            .map(([id, label]) => ({ value: id, label }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [selectedRightsholders, sponsorMap]);

    const countryList = filters?.countries?.list ?? {};
    const broadcastCountryOptions = useMemo(
        () =>
            Object.entries(countryList)
                .map(([value, country]: [string, any]) => ({
                    value: Number(value),
                    label: country.name,
                }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [countryList],
    );

    const normalizedBroadcastCountries = normalizeBroadcastCountries(
        values.broadcastCountryIds,
        countryList,
        values.broadcastCountryNames,
    );

    // Generic Set Form Values
    const setFormValue = fieldName => value =>
        setFieldValue(fieldName, value, false);

    const providerValue = {
        setFormValue,
        client,
        rightsholderAttribution,
        goalsOptions,
        rightsholdersOptions,
        sponsorOptions,
        hasReportsBroadcastCountryFilter,
        broadcastCountryOptions,
        normalizedBroadcastCountries,
        shouldShowLeagueHierarchy: filters.shouldShowLeagueHierarchy,
        isLoadingFilters: filters.isLoading,
        rhTreeData,
        selectedRightsholders,
        placementOptions,
        filteredPlacements,
        isLoadingPlacements,
        availableMedia,
        setAvailableMedia,
        isLoadingPosts,
        setIsLoadingPosts,
        searchDone,
        setSearchDone,
        sendNotification,
        clearAllNotifs,
        isApplyingGoal,
        closeDrawer,
        showDrawer,
        isDrawerOpen,
        onChangeRightsholders,
        addScheduledReport,
        setActiveTable,
        user,
        scheduleHookConfig,
        openScheduleModalForCreate,
        closeScheduleModal,
        isCreateMode,
    };

    return (
        <ReportsContext.Provider value={providerValue}>
            {children}
        </ReportsContext.Provider>
    );
};

export default connect(
    ['filters', 'client', 'user'],
    combineActions({ sendNotification, clearAllNotifs }),
)(ReportsProvider);
